<template>
  <div class="mouse px">
    <c-mouse-electricity />
    <div class="fixed-panel px">
      <div class="mouse-panel info">
        <div
          class="keys"
          :style="{
            transform: `rotateX(${rotateX}) rotateY(${rotateY})`,
            transformOrigin: 'center',
          }"
        >
          <div
            class="key"
            v-for="(key, i) in keys"
            :key="key.key"
            :class="{
              ['key' + (i + 1)]: true,
              active: key.key === activeKey,
              disabled: keyDisabled(key.key),
            }"
            @click="onMouseClick(key.key)"
          >
            {{ keyDesc(key.key) }}
          </div>
        </div>
      </div>
      <div class="radio-panel">
        <a-radio-group class="virtual-radio" v-model="type" size="large">
          <a-radio-button key="keymap" value="keymap">
            {{ $t("keymap") }}
          </a-radio-button>
          <a-radio-button key="macro" value="macro">
            {{ $t("macro") }}
          </a-radio-button>
          <a-radio-button key="dpi" value="dpi">
            DPI
          </a-radio-button>
          <a-radio-button key="param" value="param">
            {{ $t("mouse.param") }}
          </a-radio-button>
          <a-radio-button key="light" value="light">
            {{ $t("mouse.light") }}
          </a-radio-button>
          <a-radio-button key="reset" value="reset" @click="onReset">
            {{ $t("reset") }}
          </a-radio-button>
          <a-radio-button key="upload" value="upload" @click="onUpload">
            <a-tooltip>
              <template slot="title">
                <span style="color: #fff;">{{ $t("downloadTip") }}</span>
              </template>
              {{ $t("download") }}
            </a-tooltip>
          </a-radio-button>
          <a-radio-button key="download" value="download" @click="onDownload">
            <a-tooltip>
              <template slot="title">
                <span style="color: #fff;">{{ $t("saveTip") }}</span>
              </template>
              {{ $t("save") }}
            </a-tooltip>
          </a-radio-button>
        </a-radio-group>
      </div>
    </div>
    <div class="settings-panel">
      <div class="panels" v-if="type === 'keymap'">
        <div class="panel">
          <div class="panel-content">
            <div class="title">{{ $t("mouse.mouseKeys") }}</div>
            <a-radio-group
              :value="selectedKey"
              @change="onSelectedKey"
              class="radio-group"
            >
              <a-radio
                class="radio"
                v-for="key in mouseKeys"
                :key="key.key.toString()"
                :value="key.key.toString()"
              >
                {{ $t("mouse.macroDesc." + key.desc) }}
              </a-radio>
            </a-radio-group>
          </div>
        </div>
        <div class="panel">
          <div class="panel-content">
            <div class="title">{{ $t("mouse.basicKeys") }}</div>
            <a-radio-group
              :value="selectedKey"
              @change="onSelectedKey"
              class="radio-group"
            >
              <a-radio
                class="radio"
                v-for="key in basicKeys"
                :key="key.key.toString()"
                :value="key.key.toString()"
              >
                {{ $t("mouse.macroDesc." + key.desc) }}
              </a-radio>
            </a-radio-group>
          </div>
        </div>
        <div class="panel" :class="{ hotkey: hotkeyOrMacro === 'hotkey' }">
          <div class="panel-content">
            <div class="title">
              <div
                class="half"
                :class="{ active: hotkeyOrMacro === 'hotkey' }"
                @click="hotkeyOrMacro = 'hotkey'"
              >
                {{ $t("mouse.hotkey") }}
              </div>
              <div
                class="half"
                :class="{ active: hotkeyOrMacro === 'macro' }"
                @click="hotkeyOrMacro = 'macro'"
              >
                {{ $t("mouse.macro") }}
              </div>
            </div>
            <template v-if="hotkeyOrMacro === 'hotkey'">
              <a-radio-group
                :value="
                  keyMatrix[activeKey][0] === '0x00'
                    ? keyMatrix[activeKey][1]
                    : null
                "
                @change="onSelectedHotkey(1, $event.target.value)"
                class="radio-group"
              >
                <a-radio class="radio" value="0xe0"> Ctrl </a-radio>
                <a-radio class="radio" value="0xe1"> Shift </a-radio>
                <a-radio class="radio" value="0xe2"> Alt </a-radio>
                <a-radio class="radio" value="0xe3"> Code </a-radio>
              </a-radio-group>
              <span style="font-size: 0.3rem;">+</span>
              <div class="checkbox-group">
                <md-checkbox
                  class="checkbox"
                  v-model="keyMatrix[activeKey][2]"
                  @change="onSelectedHotkey(2, $event)"
                  value="0xe0"
                >
                  Ctrl
                </md-checkbox>
                <md-checkbox
                  class="checkbox"
                  v-model="keyMatrix[activeKey][2]"
                  @change="onSelectedHotkey(2, $event)"
                  value="0xe1"
                >
                  Shift
                </md-checkbox>
                <md-checkbox
                  class="checkbox"
                  v-model="keyMatrix[activeKey][2]"
                  @change="onSelectedHotkey(2, $event)"
                  value="0xe2"
                >
                  Alt
                </md-checkbox>
                <md-checkbox
                  class="checkbox"
                  v-model="keyMatrix[activeKey][2]"
                  @change="onSelectedHotkey(2, $event)"
                  value="0xe3"
                >
                  Code
                </md-checkbox>
              </div>
              <span style="font-size: 0.3rem;">+</span>
              <c-mouse-virtual-keyboard
                :selectedKey="keyMatrix[activeKey][3]"
                @change="onSelectedHotkey(3, $event)"
              />
              <div class="sub-title">
                {{ $t("mouse.firepowerKey") }}
              </div>
              <c-mouse-virtual-keyboard
                :selectedKey="
                  keyMatrix[activeKey][0] === '0x0b'
                    ? keyMatrix[activeKey][1]
                    : null
                "
                @change="onSelectedFirepower(1, $event)"
                showMouse
              />
              {{ $t("mouse.interval") }}
              <md-field class="repeat">
                <md-input
                  class="repeat"
                  :value="
                    keyMatrix[activeKey][0] === '0x0b'
                      ? Number(keyMatrix[activeKey][2])
                      : null
                  "
                  @change="onSelectedFirepower(2, $event.target.value)"
                  type="number"
                  min="1"
                  max="255"
                  step="1"
                  oninput="this.value = this.value.replace(/[^0-9]/g, '');
                  this.value = this.value > 255 ? 255 : this.value;
                  this.value = this.value < 1 ? 1 : this.value;"
                ></md-input>
                <span class="md-suffix">ms</span>
              </md-field>
              <span style="color: transparent;">---</span>
              {{ $t("mouse.repeat") }}
              <md-field class="repeat">
                <md-input
                  class="repeat"
                  :value="
                    keyMatrix[activeKey][0] === '0x0b'
                      ? Number(keyMatrix[activeKey][3])
                      : null
                  "
                  @change="onSelectedFirepower(3, $event.target.value)"
                  type="number"
                  min="0"
                  max="255"
                  step="1"
                  oninput="this.value = this.value.replace(/[^0-9]/g, '');
                  this.value = this.value > 255 ? 255 : this.value;
                  this.value = this.value < 0 ? 0 : this.value;"
                ></md-input>
              </md-field>
            </template>
            <template v-else>
              <a-radio-group
                :value="selectedMacroIndex"
                @change="onSelectedMacro"
                class="radio-group"
              >
                <a-radio
                  class="radio"
                  v-for="macro in macroData"
                  :key="macro.index"
                  :value="macro.index"
                >
                  {{ macro.macro_name }}
                </a-radio>
              </a-radio-group>
              <a-radio-group
                :value="playModel"
                @change="onSelectedPlayModel"
                class="play-model"
                v-show="selectedMacroIndex >= 0"
              >
                <a-radio class="radio" value="0x00">
                  {{ $t("mouse.repeat") }}
                  <md-field class="repeat">
                    <md-input
                      class="repeat"
                      :value="repeat"
                      @change="onRepeatChange"
                      :disabled="playModel !== '0x00'"
                      type="number"
                      min="1"
                      max="65535"
                      step="1"
                      oninput="this.value = this.value.replace(/[^0-9]/g, '');
                  this.value = this.value > 65535 ? 65535 : this.value;
                  this.value = this.value < 1 ? 1 : this.value;"
                    ></md-input>
                  </md-field>
                </a-radio>
                <a-radio class="radio" value="0x01">
                  {{ $t("mouse.auto") }}
                </a-radio>
                <a-radio class="radio" value="0x02">
                  {{ $t("mouse.press") }}
                </a-radio>
              </a-radio-group>
            </template>
          </div>
        </div>

        <div class="buttons">
          <div class="button" @click="onDisable">{{ $t("mouse.disable") }}</div>
          <div class="button" @click="onRestore">{{ $t("mouse.restore") }}</div>
        </div>
      </div>
      <c-mouse-macro v-if="type === 'macro'" />
      <c-mouse-dpi v-if="type === 'dpi'" />
      <c-mouse-param v-if="type === 'param'" />
      <c-mouse-light v-if="type === 'light'" />
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import { keys, mouseKeys, basicKeys, allKeys } from "./mouse";
import VirtualKeyboard from "./components/VirtualKeyboard";

export default {
  data() {
    return {
      keys,
      mouseKeys,
      basicKeys,
      allKeys,
      rotateX: "0deg",
      rotateY: "0deg",
      type: "keymap",
      activeKey: "0x01",
      selectedKey: "0x01,0x00,0xf1,0x00",
      hotkeyOrMacro: "hotkey",
      selectedMacroIndex: -1,
      playModel: "0x00",
      repeat: 1,
    };
  },
  computed: {
    ...mapState("assets", {
      images: "images",
    }),
    ...mapState("loader", {
      inLoader: "inLoader",
      systemInfo: "systemInfo",
      currentDevice: "currentDevice",
    }),
    ...mapState("mouse", {
      profile: "profile",
      keyMatrix: "keyMatrix",
      macroData: "macroData",
      DPI: "DPI",
      param: "param",
      light: "light",
    }),
  },
  methods: {
    keyDesc(k) {
      let desc = "";
      const key = this.keyMatrix[k];
      const allKeys = VirtualKeyboard.allKeys;
      if (key[0] === "0x0b") {
        desc =
          this.$t("mouse.firepowerKey") +
          " [" +
          allKeys[key[1]].text.replace("<br/>", "") +
          "]";
      } else if (key[0] === "0x00" && key[1] !== "0x00") {
        desc = allKeys[key[1]].text.replace("<br/>", "");
        if (key[2] !== "0x00")
          desc += " + " + allKeys[key[2]].text.replace("<br/>", "");
        if (key[3] !== "0x00")
          desc += " + " + allKeys[key[3]].text.replace("<br/>", "");
      } else if (key[0] === "0x09") {
        const macroIndex = parseInt(key[2], 16);
        const macro = this.macroData.find((i) => i.index === macroIndex);
        desc =
          this.$t("mouse.macro") +
          (macro ? " " + macro.macro_name : " M_" + macroIndex);
      } else {
        const targetKey = this.allKeys.find(
          (i) => i.key.toString() === key.toString()
        );
        desc = targetKey ? this.$t("mouse.macroDesc." + targetKey.desc) : "";
      }
      return desc;
    },
    keyDisabled(key) {
      if (
        this.keyMatrix[key].toString() ===
          ["0x01", "0x00", "0xf0", "0x00"].toString() &&
        Object.values(this.keyMatrix).filter(
          (value) =>
            value.toString() === ["0x01", "0x00", "0xf0", "0x00"].toString()
        ).length <= 1
      ) {
        return true;
      }
      return false;
    },
    onMouseClick(key) {
      if (this.keyDisabled(key)) return;
      this.activeKey = key;
      if (
        this.keyMatrix[key][0] === "0x00" &&
        this.keyMatrix[key][1] !== "0x00"
      ) {
        this.selectedKey = "0x00,0x00,0x00,0x00";
        this.selectedMacroIndex = -1;
      } else if (this.keyMatrix[key][0] === "0x09") {
        this.selectedKey = "0x00,0x00,0x00,0x00";
        this.selectedMacroIndex = parseInt(this.keyMatrix[key][2], 16);
        this.playModel = this.keyMatrix[key][1];
        const macro = this.macroData.find(
          (i) => i.index === this.selectedMacroIndex
        );
        this.repeat = macro.repeat;
      } else {
        this.selectedKey = this.keyMatrix[key].toString();
        this.selectedMacroIndex = -1;
      }
    },
    onSelectedKey(e) {
      if (!this.activeKey) return;
      const keyMatrix = { ...this.keyMatrix };
      this.selectedKey = e.target.value;
      this.selectedMacroIndex = -1;
      keyMatrix[this.activeKey] = this.selectedKey.split(",");
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    onSelectedFirepower(index, value) {
      const keyMatrix = { ...this.keyMatrix };
      this.selectedKey = "0x00,0x00,0x00,0x00";
      this.selectedMacroIndex = -1;
      if (keyMatrix[this.activeKey][0] !== "0x0b") {
        keyMatrix[this.activeKey] = ["0x0b", "0x04", "0x01", "0x01"];
      }
      if (!value.includes("0x"))
        value = `0x${Number(value).toString(16).padStart(2, "0")}`;
      keyMatrix[this.activeKey][index] = value;
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    onSelectedHotkey(index, value) {
      if (!this.activeKey) return;
      const keyMatrix = { ...this.keyMatrix };
      this.selectedKey = "0x00,0x00,0x00,0x00";
      this.selectedMacroIndex = -1;
      if (keyMatrix[this.activeKey][0] !== "0x00") {
        keyMatrix[this.activeKey] = ["0x00", "0xe0", "0x00", "0x04"];
      }
      keyMatrix[this.activeKey][index] = value || "0x00";
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    onSelectedMacro(e) {
      if (!this.activeKey) return;
      const keyMatrix = { ...this.keyMatrix };
      this.selectedKey = "0x00,0x00,0x00,0x00";
      this.selectedMacroIndex = Number(e.target.value);
      keyMatrix[this.activeKey] = [
        "0x09",
        this.playModel,
        `0x${this.selectedMacroIndex.toString(16).padStart(2, "0")}`,
        "0x00",
      ];
      const macro = this.macroData.find(
        (i) => i.index === this.selectedMacroIndex
      );
      this.repeat = macro.repeat;
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    onSelectedPlayModel(e) {
      const keyMatrix = { ...this.keyMatrix };
      this.repeat = 1;
      this.playModel = e.target.value;
      keyMatrix[this.activeKey][1] = this.playModel;
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    onRepeatChange(e) {
      this.repeat = e.target.value;
      this.onSure();
    },
    onDisable() {
      const keyMatrix = { ...this.keyMatrix };
      keyMatrix[this.activeKey] = ["0x00", "0x00", "0x00", "0x00"];
      this.selectedKey = "0x00,0x00,0x00,0x00";
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    onRestore() {
      const keyMatrix = {
        "0x00": ["0x01", "0x00", "0xf0", "0x00"],
        "0x02": ["0x01", "0x00", "0xf2", "0x00"],
        "0x01": ["0x01", "0x00", "0xf1", "0x00"],
        "0x0e": ["0x01", "0x00", "0xf5", "0x01"],
        "0x0f": ["0x01", "0x00", "0xf5", "0xff"],
        "0x03": ["0x01", "0x00", "0xf4", "0x00"],
        "0x04": ["0x01", "0x00", "0xf3", "0x00"],
      };
      this.$store.dispatch("mouse/setKeyMatrix", {
        ...this.keyMatrix,
        [this.activeKey]: keyMatrix[this.activeKey],
      });
      this.$store.dispatch("loader/changeMouseKey", {
        key_code: this.activeKey,
        change_key: keyMatrix[this.activeKey],
      });
    },
    onSure() {
      if (this.keyMatrix[this.activeKey][0] === "0x0b") {
        const change_key = [...this.keyMatrix[this.activeKey]];
        this.$store.dispatch("loader/changeMouseKey", {
          key_code: this.activeKey,
          change_key,
        });
      } else if (
        this.keyMatrix[this.activeKey][0] === "0x00" &&
        this.keyMatrix[this.activeKey][1] !== "0x00"
      ) {
        const change_key = [...this.keyMatrix[this.activeKey]];
        if (change_key[3] != "0x00" && change_key[2] == "0x00") {
          change_key[2] = change_key[3];
          change_key[3] = "0x00";
        }
        this.$store.dispatch("loader/changeMouseKey", {
          key_code: this.activeKey,
          change_key,
        });
      } else if (this.selectedMacroIndex > -1) {
        let macro = this.macroData.find(
          (i) => i.index === this.selectedMacroIndex
        );
        macro.key_code = this.activeKey;
        macro.repeat = parseInt(this.repeat);
        macro.playModel = this.playModel;
        macro.change_key = [
          "0x09",
          this.playModel,
          `0x${macro.index.toString(16).padStart(2, "0")}`,
          "0x00",
        ];
        this.$store.dispatch("loader/setMouseMacro", macro);
        // this.$store.dispatch("mouse/setMacroData", [...this.macroData]);
      } else {
        this.$store.dispatch("loader/changeMouseKey", {
          key_code: this.activeKey,
          change_key: this.selectedKey.split(","),
        });
      }
    },
    onReset() {
      this.$confirm({
        title: this.$t("mouse.reset"),
        okText: this.$t("yes"),
        cancelText: this.$t("cancel"),
        centered: true,
        onOk: () => {
          this.$store.dispatch("loader/setLoading", true);
          this.$store.dispatch("loader/mouseReset");
          this.$store.dispatch("mouse/setKeyMatrix", {
            "0x00": ["0x01", "0x00", "0xf0", "0x00"],
            "0x02": ["0x01", "0x00", "0xf2", "0x00"],
            "0x01": ["0x01", "0x00", "0xf1", "0x00"],
            "0x0e": ["0x01", "0x00", "0xf5", "0x01"],
            "0x0f": ["0x01", "0x00", "0xf5", "0xff"],
            "0x03": ["0x01", "0x00", "0xf4", "0x00"],
            "0x04": ["0x01", "0x00", "0xf3", "0x00"],
          });
          this.$store.dispatch("mouse/setDPI", {
            profile: 0,
            currentID: 0,
            isOpen: 1, //1:开启x-y同步，0：关闭x-y同步
            DPItime: [
              800,
              1600,
              2400,
              3200,
              5000,
              10000,
              20000,
              this.param.mouseID === 1935 ? 26000 : 30000,
            ], // （50~26000）
            xDPI: [
              800,
              1600,
              2400,
              3200,
              5000,
              10000,
              20000,
              this.param.mouseID === 1935 ? 26000 : 30000,
            ], // （50~26000）
            yDPI: [
              800,
              1600,
              2400,
              3200,
              5000,
              10000,
              20000,
              this.param.mouseID === 1935 ? 26000 : 30000,
            ], // （50~26000）
            DPIcolor: [
              "#FF0000",
              "#00FF00",
              "#0000FF",
              "#00FFFF",
              "#800080",
              "#FFFF00",
              "#FF00FF",
              "#ffffff",
            ],
          });
          this.$store.dispatch("mouse/setParam", {
            profile: 0,
            keySensitivity: 0,
            USBDebounce: 3,
            twoPointFourGDebounce: 1,
            BTDebounce: 1,
            buttonChangeTime: 12,
            wheelToButton: 1,
            buttonToWheel: 1,
            sleepOneTime24G: 30,
            sleepOneTimeBT: 600,
            reportRate: 1000,
            liftCutOff: 1,
            motionSync: 1,
            angleSnap: 0,
            rippleCorrection: 0,
            charLight: [255, 255, 255],
            charEndLight: [255, 0, 255],
            charLightswitch: 1,
            mouseID: this.param.mouseID,
          });
          this.$message.info(this.$t("loader.saveSuccess"));
        },
      });
    },
    onUpload() {
      setTimeout(() => (this.type = ""), 10);
      this.$store.dispatch("loader/setLoading", { loading: true, time: 15000 });
      setTimeout(() => {
        this.$store.dispatch("loader/loadMousejson");
      }, 100);
    },
    onDownload() {
      setTimeout(() => (this.type = ""), 10);

      const KeyMatrix = { ...this.keyMatrix };
      for (const key in KeyMatrix) {
        let item = KeyMatrix[key];
        if (item[0] == "0x00" && item[3] != "0x00" && item[2] == "0x00") {
          item[2] = item[3];
          item[3] = "0x00";
        }
      }
      this.$store.dispatch("loader/setLoading", true);
      this.$store.dispatch("loader/saveMousejson", {
        profile: this.profile,
        KeyMatrix,
        mouseMacroData: this.macroData,
        mouseDPI: this.DPI,
        mouseArgs: this.param,
        mouseLight: this.light,
      });
    },
  },
  watch: {
    type(type) {
      if (type === "keymap") this.onMouseClick(this.activeKey);
    },
  },
  mounted() {
    this.$store.dispatch("loader/setInLoader", true);
    this.$store.dispatch("loader/initMouseData");
    this.$store.dispatch("loader/setLoading", true);
    window.initMouseData = (json) => {
      if (json) {
        console.log("updateMouseSet", json);
        const data = JSON.parse(json);
        data.mouseDPI.isOpen = 1;
        data.mouseDPI.xDPI.map((x, index) => {
          if (x != data.mouseDPI.yDPI[index]) {
            data.mouseDPI.isOpen = 0;
          }
        });
        this.$store.dispatch("mouse/setKeyMatrix", data.KeyMatrix);
        this.$store.dispatch("mouse/setMacroData", data.mouseMacroData);
        this.$store.dispatch("mouse/setDPI", data.mouseDPI);
        this.$store.dispatch("mouse/setParam", data.mouseArgs);
        this.$store.dispatch("loader/setLoading", false);
        this.onMouseClick(this.activeKey);
        console.log("updateMouseSet", "true");
      }
    };
    window.updateMouseSet = (json) => {
      if (json) {
        console.log("updateMouseSet", json);
        const data = JSON.parse(json);
        this.$store.dispatch("mouse/setKeyMatrix", data.KeyMatrix);
        console.log("updateMouseSet", "true");
      }
    };
    window.initMouseMacros = (json) => {
      if (json) {
        console.log("initMouseMacros", json);
        const data = JSON.parse(json);
        this.$store.dispatch("mouse/setMacroData", data.mouseMacroData);
        console.log("initMouseMacros", "true");
      }
    };
    window.updateMouseDPI = (json) => {
      if (json) {
        console.log("updateMouseDPI", json);
        const data = JSON.parse(json);
        data.isOpen = 1;
        data.xDPI.map((x, index) => {
          if (x != data.yDPI[index]) {
            data.isOpen = 0;
          }
        });
        this.$store.dispatch("mouse/setDPI", data);
        console.log("updateMouseDPI", "true");
      }
    };
    window.updateMouseInfo = (json) => {
      if (json) {
        console.log("updateMouseInfo", json);
        const data = JSON.parse(json);
        this.$store.dispatch("mouse/setParam", data);
        console.log("updateMouseInfo", "true");
      }
    };
    window.updateFinish = () => {
      console.log("updateFinish");
    };
    window.loadingFinish = () => {
      this.$store.dispatch("loader/setLoading", false);
      console.log("loadingFinish");
    };

    // window.addEventListener("mousemove", (event) => {
    //   this.rotateY =
    //     ((event.clientX / window.innerWidth) * 40 - 20).toFixed() + "deg";
    //   this.rotateX =
    //     ((event.clientY / window.innerHeight) * 40 - 20).toFixed() + "deg";
    //   console.log(`鼠标位置 - X: ${this.rotateX}, Y: ${this.rotateY}`);
    // });
    if (window.location.href.includes("localhost")) {
      setTimeout(() => {
        window.initMouseData(
          JSON.stringify({
            mouseMacroData: [
              {
                index: 0,
                profile: 0,
                key_code: "0x04",
                repeat: 3,
                playModel: "0x00",
                macroValue: {
                  keyOrder: ["0x111e", "0x101e"],
                  pressTime: [64, 74],
                },
                change_key: ["0x09", "0x00", "0x00", "0x00"],
              },
            ],
            mouseDPI: {
              isOpen: 0,
              profile: 0,
              currentID: 4,
              xDPI: [400, 800, 1200, 1600, 3000, 6400, 10000, 30000],
              yDPI: [400, 800, 1200, 1600, 3000, 6400, 10000, 30000],
              DPIcolor: [
                "#FF0000",
                "#FF3200",
                "#FFFF00",
                "#00FF00",
                "#00FFFF",
                "#0000FF",
                "#FF00FF",
                "#FFFFFF",
              ],
            },
            mouseArgs: {
              profile: 0,
              reportRate: 8000,
              USBDebounce: 3,
              rippleCorrection: 0,
              buttonChangeTime: 12,
              wheelToButton: 1,
              buttonToWheel: 1,
              sleepOneTimeBT: 600,
              sleepOneTime24G: 30,
              liftCutOff: 1,
              angleSnap: 0,
              mouseID: 2222,
              charLight: [255, 255, 255],
              charEndLight: [255, 255, 255],
              charLightswitch: 0,
              twoPointFourGDebounce: 1,
              BTDebounce: 1,
              motionSync: 1,
            },
            KeyMatrix: {
              "0x00": ["0x01", "0x00", "0xf0", "0x00"],
              "0x02": ["0x01", "0x00", "0xf2", "0x00"],
              "0x01": ["0x09", "0x00", "0x00", "0x00"],
              "0x0e": ["0x01", "0x00", "0xf5", "0x01"],
              "0x0f": ["0x01", "0x00", "0xf5", "0xff"],
              "0x03": ["0x01", "0x00", "0xf4", "0x00"],
              "0x04": ["0x01", "0x00", "0xf3", "0x00"],
            },
          })
        );
        this.$store.dispatch("loader/setLoading", false);
      }, 1000);
    }
  },
};
</script>
<style lang="scss" scoped>
.mouse {
  font-size: 16px;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 150px;
  background: #171717;

  .fixed-panel.px {
    width: 100%;
    background: #171717;
    padding: 60px 0 20px 0;
    position: fixed;
    z-index: 1;
    top: 0;
  }

  .mouse-panel {
    @include flex-center;

    .keys {
      width: 400px;
      height: 400px;
      background: url("../../assets/images/mouse.png") center / auto 100%
        no-repeat;
      position: relative;

      .key {
        width: 300px;
        height: 35px;
        line-height: 35px;
        position: absolute;
        font-size: 20px;
        border-bottom: 1px solid #bf94ff;
        opacity: 0.5;
        white-space: nowrap;
        cursor: pointer;

        &:hover,
        &.active {
          opacity: 1;
        }

        &.disabled {
          cursor: not-allowed;
        }

        &::before {
          display: block;
          content: "";
          width: 50px;
          height: 0;
          border-top: 1px solid #bf94ff;
          transform: rotate(45deg);
          position: absolute;
          right: 0;
          bottom: 0;
        }

        &::after {
          display: block;
          content: "";
          width: 8px;
          height: 8px;
          border-radius: 8px;
          background: #bf94ff;
          position: absolute;
          right: 0;
          bottom: 0;
        }
      }

      .key1 {
        top: -5%;
        left: -47%;

        &::before {
          right: -43px;
          bottom: -18px;
        }

        &::after {
          right: -43px;
          bottom: -43px;
        }
      }

      .key2 {
        top: 11%;
        left: 74%;
        text-align: right;

        &::before {
          width: 100px;
          transform: rotate(0);
          left: -100px;
          bottom: -1px;
        }

        &::after {
          left: -100px;
          bottom: -4px;
        }
      }

      .key3 {
        top: -5%;
        left: 74%;
        text-align: right;

        &::before {
          transform: rotate(135deg);
          left: -43px;
          bottom: -18px;
        }

        &::after {
          left: -43px;
          bottom: -43px;
        }
      }

      .key4 {
        top: 14%;
        left: -34.5%;

        &::before {
          transform: rotate(135deg);
          right: -43px;
          bottom: 17px;
        }

        &::after {
          right: -43px;
          bottom: 33px;
        }
      }

      .key5 {
        top: 27%;
        left: 60%;
        text-align: right;

        &::before {
          left: -43px;
          bottom: 17px;
        }

        &::after {
          left: -43px;
          bottom: 34px;
        }
      }

      .key6 {
        top: 31%;
        left: -62%;

        &::before {
          transform: rotate(0);
          right: -50px;
          bottom: -1px;
        }

        &::after {
          right: -50px;
          bottom: -4px;
        }
      }

      .key7 {
        top: 48%;
        left: -62%;

        &::before {
          transform: rotate(0);
          right: -50px;
          bottom: -1px;
        }

        &::after {
          right: -50px;
          bottom: -4px;
        }
      }
    }
  }

  .radio-panel {
    @include flex-center;
    width: 1200px;
    margin: 0 auto 0 auto;
    justify-content: flex-end;
    white-space: nowrap;

    .virtual-radio {
      #file {
        display: none;
      }
    }
  }

  .settings-panel {
    @include flex-center;
    margin-top: calc(500px - 4vw);
    margin-bottom: 60px;
    position: relative;
    z-index: 0;
  }

  .panels {
    @include flex-center;
    flex-wrap: wrap;
    width: 1500px;

    .buttons {
      @include flex-center;
      width: 400px;
      height: 100px;

      .button {
        @include button;
        width: 180px;
        height: 34px;
        margin: 10px;
        border: 1px solid $primary-color;
        border-radius: 5px;
        background: transparent;
        color: $primary-color;

        &:hover {
          color: #ffffff;
        }
      }
    }
  }

  .panel {
    width: 370px;
    height: 340px;
    background-image: linear-gradient(270deg, #ee92ff 0%, #42bfe9 100%);
    box-shadow: 0 0 20px 0 #4136f1;
    border-radius: 15px;
    padding: 3px;
    margin: 0 20px;

    .panel-content {
      width: 100%;
      height: 100%;
      padding: 5px 15px;
      background-color: #000000;
      border-radius: 15px;
      display: flex;
      flex-direction: column;

      .title {
        @include flex-center;
        width: 100%;
        font-size: 24px;
        line-height: 50px;
        height: 50px;
        color: $primary-color;
        flex: none;

        .half {
          width: 50%;
          height: 42px;
          text-align: center;
          color: #999999;
          cursor: pointer;

          &:hover {
            color: $primary-color;
          }
        }

        .active {
          color: $primary-color;
          border-bottom: 2px solid $primary-color;
        }
      }
    }

    .radio-group {
      width: 100%;
      height: 100%;
      overflow-y: scroll;
      flex: auto;

      &::-webkit-scrollbar {
        width: 10px;
      }

      .radio {
        @include flex-center;
        height: 50px;
        line-height: 50px;
        justify-content: flex-start;
        font-size: 20px;

        &.ant-radio-wrapper-checked,
        &:hover {
          color: #ffffff;
        }
      }
    }

    .play-model {
      @include flex-center;
      height: 60px;
      color: #ffffff;
      flex: none;

      .radio {
        @include flex-center;
        justify-content: flex-start;
      }
    }

    &.hotkey {
      .panel-content {
        flex-direction: row;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        align-content: flex-start;
        color: $primary-color;
        font-size: 16px;

        .sub-title {
          width: 100%;
          font-size: 20px;
          color: $primary-color;
          flex: none;
          margin-bottom: -10px;
        }
      }

      .radio-group {
        width: 100px;
        height: auto;
        overflow-y: auto;
        flex: none;
      }

      .checkbox-group {
        display: flex;
        flex-direction: column;
      }

      .radio,
      .checkbox {
        @include flex-center;
        height: 40px;
        line-height: 40px;
        justify-content: flex-start;
        background-color: #050505;
        color: #ffffff;
        padding: 0 5px;
        margin: 5px 5px 0 5px;
        border-radius: 5px;
        font-size: 18px;
      }
    }
  }
}
</style>

<style lang="scss">
.mouse {
  .panel {
    .radio-group .radio {
      .ant-radio .ant-radio-inner {
        border-color: $primary-color;

        &::after {
          background-color: $primary-color;
        }
      }
    }

    &.hotkey {
      .radio-group .radio {
        .ant-radio {
          margin-right: 0;
        }
      }
      .md-checkbox {
        .md-checkbox-container {
          border-radius: 2px;
          border-color: $primary-color;
        }

        label {
          padding-left: 5px;
        }
      }
      .md-checkbox.md-theme-default.md-checked .md-checkbox-container {
        background-color: $primary-color;
      }
    }

    .play-model .radio {
      .ant-radio {
        margin-right: 5px;
      }

      span:not(.ant-radio) {
        @include flex-center;
        padding: 0;
        font-size: 14px;
        color: #ffffff;
      }
    }

    .repeat {
      width: 60px;
      margin-left: 5px;
      font-size: 14px;
    }
  }
}
</style>
